import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * NPSPrompt — inline feedback widget. Shows 0–10 scale + optional comment.
 *
 * Props:
 *   category        — 'order' | 'parking' | 'movie' | 'restaurant' | 'overall'
 *   targetId        — order_id / booking_id (optional)
 *   title           — prompt headline
 *   onSubmitted()   — callback after successful submit
 */
export default function NPSPrompt({ category = 'overall', targetId, title = 'How likely are you to recommend Inorbit?', onSubmitted }) {
    const [score, setScore] = useState(null);
    const [comment, setComment] = useState('');
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    if (dismissed) return null;

    const submit = async () => {
        if (score == null) { toast.error('Please pick a score'); return; }
        setBusy(true);
        try {
            await api.post('/feedback', { category, target_id: targetId, nps: score, comment });
            setSent(true);
            onSubmitted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not submit feedback');
        } finally { setBusy(false); }
    };

    const color = (n) => (n <= 6 ? 'bg-rose-500 text-white' : n <= 8 ? 'bg-inorbit-orange text-white' : 'bg-emerald-500 text-white');

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="card-luxury p-6 relative bg-inorbit-yellow border-inorbit-yellow"
                data-testid="nps-prompt"
            >
                <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 text-inorbit-blue/60 hover:text-inorbit-blue" aria-label="Dismiss" data-testid="nps-dismiss"><X size={16} /></button>
                <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={16} className="text-inorbit-blue" />
                    <div className="overline text-inorbit-blue">Quick feedback</div>
                </div>
                {sent ? (
                    <div className="font-serif text-xl text-inorbit-blue">Thank you 🙏 Your feedback shapes Inorbit.</div>
                ) : (
                    <>
                        <div className="font-serif text-xl text-inorbit-blue leading-snug mb-4">{title}</div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {Array.from({ length: 11 }, (_, n) => (
                                <button
                                    key={n} onClick={() => setScore(n)}
                                    className={`w-10 h-10 rounded font-semibold text-sm transition-all ${score === n ? `${color(n)} scale-110` : 'bg-white text-inorbit-blue hover:bg-inorbit-blue/10'}`}
                                    data-testid={`nps-score-${n}`}
                                >{n}</button>
                            ))}
                        </div>
                        <div className="flex justify-between text-[10px] uppercase tracking-widest text-inorbit-blue/70 mb-3">
                            <span>Not likely</span><span>Very likely</span>
                        </div>
                        {score != null && (
                            <>
                                <textarea
                                    rows={2}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Tell us why (optional)"
                                    className="input-box w-full mb-3 bg-white"
                                    maxLength={400}
                                    data-testid="nps-comment"
                                />
                                <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="nps-submit">
                                    {busy ? 'Sending…' : 'Submit feedback'}
                                </button>
                            </>
                        )}
                    </>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
